import {
    AnalyticsMembershipEvent,
    type MoneyMap,
    MoneyMapAdvisoryMode,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/react-bindings';
import { router } from 'expo-router';

import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { requestBuilder } from '../../http/RequestBuilder';
import {
    captureException,
    captureWarning,
    trackForMembership,
} from '../../logger/Logger';
import { useUpdateMemberJourneyLog } from '../../member-profile/hooks/useUpdateMemberJourneyLog';
import {
    type ApexAccountsStepCompleteness,
    type GoWorkflowStep,
    GoWorkflowStepType,
    type ImTeaserStepCompleteness,
    type MoneyMapCompleteness,
} from '../types/GoWorkflow';
import useGetActiveMoneyMap from './useGetActiveMoneyMap';

const fetchMoneyMapCompleteness = requestBuilder.query<
    MoneyMapCompleteness,
    { moneyMapId: string }
>({
    endpoint: ({ moneyMapId }) => `/api/money-map/${moneyMapId}/completeness`,
    queryKey: ({ moneyMapId }) => `getMoneyMapCompleteness/${moneyMapId}`,
});

// Useful for initializing state while waiting for server response
const initialMoneyMapCompleteness: MoneyMapCompleteness = {
    reviewMoneyMapStep: {
        isIncluded: true,
        isEnabled: true,
        isCompleteForMember: false,
        isCompleteForMembership: false,
        memberHasReviewed: false,
    },
    cashAccountsStep: {
        isIncluded: true,
        isEnabled: false,
        isCompleteForMember: false,
        isCompleteForMembership: false,
        jointAccountsToOpen: [],
        memberSoloAccountsToOpen: [],
        memberJointAccountsToAccept: [],
        partnerSoloAccountsToOpen: [],
        partnerJointAccountsToAccept: [],
    },
    cardAccountsStep: {
        isIncluded: true,
        isEnabled: false,
        isCompleteForMember: false,
        isCompleteForMembership: false,
        jointAccountsToOpen: [],
        memberSoloAccountsToOpen: [],
        memberJointAccountsToAccept: [],
        partnerSoloAccountsToOpen: [],
        partnerJointAccountsToAccept: [],
    },
    apexAccountsStep: {
        isIncluded: false,
        isEnabled: false,
        isCompleteForMember: false,
        isCompleteForMembership: false,
        areAllAccountsInGoodStanding: true,
        isCompletionPendingInvestigationRun: false,
        jointAccountsToOpen: [],
        memberSoloAccountsToOpen: [],
        memberJointAccountsToAccept: [],
        partnerSoloAccountsToOpen: [],
        partnerJointAccountsToAccept: [],
    },
    incomeSplitStep: {
        isIncluded: true,
        isEnabled: false,
        isCompleteForMember: false,
        isCompleteForMembership: false,
        incomeSplitCreated: false,
    },
};

const getNextStep = (
    currentStepType: GoWorkflowStepType,
    workflowSteps: GoWorkflowStep[],
): GoWorkflowStep | null => {
    const currentStep = workflowSteps.find(
        (step) => step.type === currentStepType,
    );
    if (currentStep == null) {
        captureException(new Error('Current step not found in workflow steps'));
        return null;
    }

    // Walk forward past steps that are already complete (even if not enabled)
    // to find the next actionable step. This handles cases where an
    // intermediate step (e.g. apex accounts) is included but disabled while
    // already marked complete — without this, the member gets stuck.
    for (let j = currentStep.index + 1; j < workflowSteps.length; j++) {
        const candidate = workflowSteps[j];
        if (candidate == null) continue;

        if (candidate.completeness.isCompleteForMember) {
            continue;
        }

        if (!candidate.completeness.isEnabled) {
            captureWarning(
                `useMoneyMapWorkflow's getNextStep called, but next step ${candidate.type} is not enabled`,
            );
            return null;
        }

        return candidate;
    }

    return null;
};

const proceedForward = (
    currentStepType: GoWorkflowStepType,
    workflowSteps: GoWorkflowStep[],
    goyoEnabled: boolean,
) => {
    const nextStep = getNextStep(currentStepType, workflowSteps);
    if (nextStep) {
        router.push(nextStep.url);
    } else if (isWorkflowCompleted(workflowSteps)) {
        captureWarning(
            `useMoneyMapWorkflow's proceedForward called, but workflow is completed`,
        );
        // GOYO: completing the Go button flow returns the member to Home
        // rather than the Money System tab.
        router.push(goyoEnabled ? '/home' : '/home/roadmap');
    } else {
        captureWarning(
            `useMoneyMapWorkflow's proceedForward called, but no valid next step`,
        );
        router.push('/roadmap/go/start');
    }
};

const isWorkflowCompleted = (workflowSteps: GoWorkflowStep[]) => {
    return workflowSteps.every(
        (step) => step.completeness.isCompleteForMembership,
    );
};

const isMoneyMapComplete = (completeness: MoneyMapCompleteness) => {
    const {
        apexAccountsStep,
        cardAccountsStep,
        cashAccountsStep,
        incomeSplitStep,
        reviewMoneyMapStep,
    } = completeness;
    return [
        apexAccountsStep,
        cardAccountsStep,
        cashAccountsStep,
        incomeSplitStep,
        reviewMoneyMapStep,
    ]
        .filter((step) => step?.isIncluded === true)
        .every((step) => step?.isCompleteForMembership);
};

export const useMoneyMapWorkflow = () => {
    const { value: enableApexAccounts } = useFeatureGate(
        FeatureFlags.ENABLE_IM_TAB,
    );
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const { moneyMap, isLoadingMoneyMap, hasMoneyMapError } =
        useGetActiveMoneyMap();

    const {
        data: fetchedMoneyMapCompleteness,
        isLoading: isLoadingMoneyMapCompleteness,
        isError: hasMoneyMapCompletenessError,
        refetch: refetchMoneyMapCompleteness,
    } = fetchMoneyMapCompleteness(
        {
            moneyMapId: moneyMap?.id ?? '',
        },
        {
            enabled: moneyMap?.id != null,
        },
    );

    const { updateMemberJourneyLog } = useUpdateMemberJourneyLog();

    const trackWorkflowCompletedEvent = (
        currentStepType: GoWorkflowStepType,
        workflowSteps: GoWorkflowStep[],
        moneyMap: MoneyMap | undefined,
    ) => {
        // Check if current step is the last one
        if (
            moneyMap &&
            workflowSteps?.[workflowSteps.length - 1]?.type === currentStepType
        ) {
            void updateMemberJourneyLog({ completedMoneyMap: true });
            trackForMembership(
                AnalyticsMembershipEvent.MONEY_MAP_WORKFLOW_COMPLETE,
                {
                    step_money_map_completed_on: currentStepType,
                    steps_in_this_money_map: workflowSteps
                        .map((s) => s.type as string)
                        .join(' >> '),
                    moneyMapId: moneyMap.id,
                    moneyMapTitle: moneyMap.title,
                    moneyMapCreatedAt: moneyMap.createdAt,
                },
            );
        }
    };

    if (hasMoneyMapError) {
        captureException(
            new Error(
                'Money Map progress could not be determined - error fetching Money Map',
            ),
        );
    }

    if (hasMoneyMapCompletenessError) {
        captureException(
            new Error(
                'Money Map progress could not be determined - error fetching Money Map Completeness',
            ),
        );
    }

    const moneyMapCompleteness =
        fetchedMoneyMapCompleteness ?? initialMoneyMapCompleteness;

    const moneyMapStep: GoWorkflowStep = {
        type: GoWorkflowStepType.MONEY_MAP,
        url: '/roadmap/go/money-map',
        completeness: moneyMapCompleteness.reviewMoneyMapStep,
        index: -1,
    };
    const cashAccountsStep: GoWorkflowStep = {
        type: GoWorkflowStepType.CASH_ACCOUNTS,
        url: '/roadmap/go/cash-accounts',
        completeness: moneyMapCompleteness.cashAccountsStep,
        index: -1,
    };
    const cardAccountsStep: GoWorkflowStep = {
        type: GoWorkflowStepType.CARD_ACCOUNTS,
        url: '/roadmap/go/card-accounts',
        completeness: moneyMapCompleteness.cardAccountsStep,
        index: -1,
    };
    const apexAccountsStep: GoWorkflowStep = {
        type: GoWorkflowStepType.APEX_ACCOUNTS,
        url: '/roadmap/go/investment-accounts',
        completeness:
            (moneyMapCompleteness.apexAccountsStep as ApexAccountsStepCompleteness) ??
            initialMoneyMapCompleteness.apexAccountsStep,
        index: -1,
    };

    const cmmImStep: GoWorkflowStep = {
        type: GoWorkflowStepType.CMM_IM,
        url: '/roadmap/go/im-info',
        completeness: {
            isCompleteForMember: false,
            isCompleteForMembership: false,
            isEnabled: true,
            isIncluded: true,
        } as const satisfies ImTeaserStepCompleteness,
        index: -1,
    };

    const incomeSplitStep: GoWorkflowStep = {
        type: GoWorkflowStepType.INCOME_SPLIT,
        url: '/roadmap/go/income-split',
        completeness: moneyMapCompleteness.incomeSplitStep,
        index: -1,
    };

    const workflowSteps: GoWorkflowStep[] = [moneyMapStep];

    if (moneyMapCompleteness.cashAccountsStep.isIncluded) {
        workflowSteps.push(cashAccountsStep);
    }

    if (moneyMapCompleteness.cardAccountsStep.isIncluded) {
        workflowSteps.push(cardAccountsStep);
    }

    // This step is injected client side if the money map was created from a
    // CMM and has the ROBO_ADVISOR_NO_IM advisory mode.  It is not displayed
    // in the list of steps but the screen is always shown before a member sees
    // the income split screen.
    if (
        moneyMap?.advisoryMode === MoneyMapAdvisoryMode.ROBO_ADVISOR_NO_IM &&
        moneyMapCompleteness.incomeSplitStep.isIncluded &&
        !isMoneyMapComplete(moneyMapCompleteness)
    ) {
        workflowSteps.push(cmmImStep);
    }

    if (
        enableApexAccounts &&
        moneyMapCompleteness.apexAccountsStep?.isIncluded
    ) {
        workflowSteps.push(apexAccountsStep);
    }

    if (moneyMapCompleteness.incomeSplitStep.isIncluded) {
        workflowSteps.push(incomeSplitStep);
    }

    workflowSteps.forEach((step, index) => {
        step.index = index;
    });

    // Find the first incomplete step
    const nextStepToComplete =
        workflowSteps.find((step) => !step.completeness.isCompleteForMember) ??
        null;

    return {
        isLoadingMoneyMap,
        moneyMap,
        hasMoneyMapError,
        isLoadingCompleteness:
            isLoadingMoneyMap || isLoadingMoneyMapCompleteness,
        moneyMapCompleteness:
            moneyMapCompleteness ?? initialMoneyMapCompleteness,
        refetchMoneyMapCompleteness,
        workflowSteps,
        nextStepToComplete,
        isWorkflowCompleted: isWorkflowCompleted(workflowSteps),
        trackWorkflowCompletedEvent: (currentStepType: GoWorkflowStepType) => {
            trackWorkflowCompletedEvent(
                currentStepType,
                workflowSteps,
                moneyMap,
            );
        },
        getNextStep: (currentStepType: GoWorkflowStepType) => {
            return getNextStep(currentStepType, workflowSteps);
        },
        proceedForward: (currentStepType: GoWorkflowStepType) => {
            proceedForward(currentStepType, workflowSteps, goyoEnabled);
        },
    };
};
